// Shared sanitizer for distributor-authored EXTERNAL link URLs before they
// become an anchor `href` on a public surface (the storefront footer legal
// links; reusable by any future external-link surface).
//
// WHY THIS EXISTS (XSS / scheme-injection boundary):
// The privacy/refund policy URLs are authored by the distributor in the
// store-editor Tax & Legal section and persisted verbatim on the store
// (stores.privacy_policy_url / refund_policy_url). They are craftable
// directly via the API, so any RENDER sink that turns them into a
// clickable link must reject dangerous schemes (`javascript:`, `data:`,
// `vbscript:`, ...) -- otherwise a stored `javascript:` URL would execute
// on click. These are meant to be absolute external policy pages, so we
// require an explicit http/https scheme (no relative-path allowance).
//
// Mirrors the scheme check in StorefrontViewShopper.sanitizeClickThroughUrl,
// but is stricter: footer legal links are always external, so protocol is
// mandatory and only http/https pass.

/**
 * Return the trimmed URL if it is a safe absolute http(s) URL, else null.
 *
 * @param {*} raw distributor-authored URL string (or null/undefined)
 * @returns {string|null} the original trimmed URL when safe, otherwise null
 */
export function sanitizeExternalUrl(raw) {
  if (!raw || typeof raw !== "string") {
    return null;
  }
  const trimmed = raw.trim();
  if (trimmed === "") {
    return null;
  }
  try {
    // Parse WITHOUT a base so scheme-less input does not silently inherit
    // the current origin's http(s) scheme -- a bare "example.com/privacy"
    // is treated as invalid rather than promoted to a same-origin link.
    // The URL constructor also normalizes whitespace/case tricks like
    // "JavaScript:" before the protocol check.
    const parsed = new URL(trimmed);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return trimmed;
    }
  } catch (e) {
    return null;
  }
  return null;
}

export default sanitizeExternalUrl;
